/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { RenditionEntry } from '../model/renditionEntry';
import { RenditionPaging } from '../model/renditionPaging';
import { SharedLinkEntry } from '../model/sharedLinkEntry';
import { SharedLinkPaging } from '../model/sharedLinkPaging';
import { BaseApi } from './base.api';
import { throwIfNotDefined } from '../../../assert';
import { buildCollectionParam } from '../../../alfrescoApiClient';
export class SharedlinksApi extends BaseApi {
    createSharedLink(sharedLinkBodyCreate, opts) {
        throwIfNotDefined(sharedLinkBodyCreate, 'sharedLinkBodyCreate');
        const queryParams = {
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.post({
            path: '/shared-links',
            queryParams,
            bodyParam: sharedLinkBodyCreate,
            returnType: SharedLinkEntry
        });
    }
    deleteSharedLink(sharedId) {
        throwIfNotDefined(sharedId, 'sharedId');
        const pathParams = {
            sharedId
        };
        return this.delete({
            path: '/shared-links/{sharedId}',
            pathParams
        });
    }
    emailSharedLink(sharedId, sharedLinkBodyEmail) {
        throwIfNotDefined(sharedId, 'sharedId');
        throwIfNotDefined(sharedLinkBodyEmail, 'sharedLinkBodyEmail');
        const pathParams = {
            sharedId
        };
        return this.post({
            path: '/shared-links/{sharedId}/email',
            pathParams,
            bodyParam: sharedLinkBodyEmail
        });
    }
    getSharedLink(sharedId, opts) {
        throwIfNotDefined(sharedId, 'sharedId');
        const pathParams = {
            sharedId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/shared-links/{sharedId}',
            pathParams,
            queryParams,
            returnType: SharedLinkEntry
        });
    }
    getSharedLinkContent(sharedId, opts) {
        throwIfNotDefined(sharedId, 'sharedId');
        const pathParams = {
            sharedId
        };
        const queryParams = {
            attachment: opts === null || opts === void 0 ? void 0 : opts.attachment
        };
        const headerParams = {
            'If-Modified-Since': opts === null || opts === void 0 ? void 0 : opts.ifModifiedSince,
            Range: opts === null || opts === void 0 ? void 0 : opts.range
        };
        return this.get({
            path: '/shared-links/{sharedId}/content',
            pathParams,
            queryParams,
            headerParams,
            accepts: ['application/octet-stream'],
            returnType: 'blob'
        });
    }
    getSharedLinkRendition(sharedId, renditionId) {
        throwIfNotDefined(sharedId, 'sharedId');
        throwIfNotDefined(renditionId, 'renditionId');
        const pathParams = {
            sharedId,
            renditionId
        };
        return this.get({
            path: '/shared-links/{sharedId}/renditions/{renditionId}',
            pathParams,
            returnType: RenditionEntry
        });
    }
    getSharedLinkRenditionContent(sharedId, renditionId, opts) {
        throwIfNotDefined(sharedId, 'sharedId');
        throwIfNotDefined(renditionId, 'renditionId');
        const pathParams = {
            sharedId,
            renditionId
        };
        const queryParams = {
            attachment: opts === null || opts === void 0 ? void 0 : opts.attachment
        };
        const headerParams = {
            'If-Modified-Since': opts === null || opts === void 0 ? void 0 : opts.ifModifiedSince,
            Range: opts === null || opts === void 0 ? void 0 : opts.range
        };
        return this.get({
            path: '/shared-links/{sharedId}/renditions/{renditionId}/content',
            pathParams,
            queryParams,
            headerParams,
            accepts: ['application/octet-stream'],
            returnType: 'blob'
        });
    }
    listSharedLinkRenditions(sharedId) {
        throwIfNotDefined(sharedId, 'sharedId');
        const pathParams = {
            sharedId
        };
        return this.get({
            path: '/shared-links/{sharedId}/renditions',
            pathParams,
            returnType: RenditionPaging
        });
    }
    listSharedLinks(opts) {
        const queryParams = {
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            where: opts === null || opts === void 0 ? void 0 : opts.where,
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/shared-links',
            queryParams,
            returnType: SharedLinkPaging
        });
    }
}
